import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { vehicleAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './VehicleDetail.css';

const VehicleDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { language, t } = useLanguage();
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);

    useEffect(() => {
        fetchVehicleDetail();
    }, [id]);

    const fetchVehicleDetail = async () => {
        try {
            const response = await vehicleAPI.getById(id);
            setVehicle(response.data.data);
        } catch (error) {
            console.error('Error fetching vehicle:', error);
            toast.error(t('error'));
            navigate('/vehicles');
        } finally {
            setLoading(false);
        }
    };

    const getVehicleName = () => {
        return vehicle?.[`name_${language}`] || vehicle?.name_tr;
    };

    const getVehicleDescription = () => {
        return vehicle?.[`description_${language}`] || vehicle?.description_tr;
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://elittaksibodrum.com';
        
        if (!imagePath.startsWith('/api/')) {
            if (imagePath.startsWith('/uploads/')) {
                return `${baseUrl}/api${imagePath}`;
            }
            return `${baseUrl}/api${imagePath}`;
        }
        
        return `${baseUrl}${imagePath}`;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>{t('loading')}...</p>
            </div>
        );
    }

    if (!vehicle) {
        return (
            <div className="container">
                <div className="not-found">
                    <h2>{t('noVehicles')}</h2>
                    <Link to="/vehicles" className="btn btn-primary">
                        {t('availableVehicles')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="vehicle-detail-page">
            <div className="container">
                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <Link to="/">{t('home')}</Link>
                    <span>/</span>
                    <Link to="/vehicles">{t('vehicles')}</Link>
                    <span>/</span>
                    <span>{getVehicleName()}</span>
                </div>

                <div className="vehicle-detail-container">
                    {/* Image Gallery */}
                    <div className="vehicle-gallery">
                        {vehicle.images && vehicle.images.length > 0 ? (
                            <>
                                <div className="main-image">
                                    <img 
                                        src={getImageUrl(vehicle.images[selectedImage])}
                                        alt={getVehicleName()}
                                        onError={(e) => {
                                            e.target.src = '/placeholder-vehicle.jpg';
                                        }}
                                    />
                                </div>
                                {vehicle.images.length > 1 && (
                                    <div className="thumbnail-list">
                                        {vehicle.images.map((image, index) => (
                                            <div 
                                                key={index}
                                                className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                                                onClick={() => setSelectedImage(index)}
                                            >
                                                <img 
                                                    src={getImageUrl(image)}
                                                    alt={`${getVehicleName()} ${index + 1}`}
                                                    onError={(e) => {
                                                        e.target.src = '/placeholder-vehicle.jpg';
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="vehicle-placeholder-large">
                                <span className="placeholder-icon">🚕</span>
                            </div>
                        )}
                    </div>

                    {/* Vehicle Info */}
                    <div className="vehicle-info-detail">
                        <h1 className="vehicle-title">{getVehicleName()}</h1>

                        {/* Pricing */}
                        <div className="vehicle-price-box">
                            <div className="price-row">
                                <span className="price-label">{t('hourlyRate')}:</span>
                                <div className="price-value">
                                    <span className="price-amount">{vehicle.hourly_price}</span>
                                    <span className="price-currency">TL / {t('hour')}</span>
                                </div>
                            </div>
                            {vehicle.daily_price && (
                                <div className="price-row">
                                    <span className="price-label">{t('dailyRate')}:</span>
                                    <div className="price-value">
                                        <span className="price-amount">{vehicle.daily_price}</span>
                                        <span className="price-currency">TL / {t('day')}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="vehicle-description">
                            <h3>{t('aboutVehicle')}</h3>
                            <p>{getVehicleDescription()}</p>
                        </div>

                        {/* Action Buttons */}
                        <div className="vehicle-actions-detail">
                            <Link 
                                to={`/reservation?vehicle=${vehicle.id}`}
                                className="btn btn-primary btn-large"
                            >
                                {t('bookNow')}
                            </Link>
                            <Link 
                                to="/vehicles"
                                className="btn btn-outline"
                            >
                                {t('backToVehicles')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VehicleDetail;